import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";

import { Eye, EyeClosed, MoreVertical } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import CreateAccount from "./account/Create";
import DeteleAccount from "./account/Delete";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { useCurrentSubscription } from "@/hooks/useCurrentSubscription";
import { Empty } from "@/components/Empty";
import { Skeleton } from "@/components/ui/skeleton";

export default function AccountListPage() {
  const navigate = useNavigate();
  const { canCreateAccount, status, limitReached } = useCurrentSubscription();

  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    accountId: null,
  });
  const [balances, setBalances] = useState({});
  const [ocultar, setOcultar] = useState(true);
  const [loading, setLoading] = useState(true);

  async function fetchAccounts() {
    setLoading(true);

    const { data: accs } = await supabase
      .from("accounts")
      .select("*, account_types(name)")
      .eq("status", true);

    setAccounts(accs || []);

    const { data: txs } = await supabase
      .from("transactions")
      .select("amount, type, account_id");

    const grouped: any = {};
    for (const acc of accs || []) grouped[acc.id] = acc.initial_balance || 0;
    for (const tx of txs || []) {
      if (!grouped[tx.account_id]) grouped[tx.account_id] = 0;
      if (tx.type === "income") grouped[tx.account_id] += tx.amount;
      else if (tx.type === "expense") grouped[tx.account_id] -= tx.amount;
    }
    setBalances(grouped);
    setLoading(false);
  }

  useEffect(() => {
    fetchAccounts();
  }, []);

  async function onSubmit(values) {
    if (editAccount) {
      await supabase.from("accounts").update(values).eq("id", editAccount.id);
    } else {
      await supabase.from("accounts").insert(values);
    }
    setOpen(false);
    setEditAccount(null);
    fetchAccounts();
  }

  async function onDelete(id: any) {
    setConfirmDialog({ open: true, accountId: id });
  }

  function onEdit(account: any) {
    setEditAccount(account);
    setOpen(true);
  }

  const limpiar = (val: any) => {
    setOpen(val);
    setEditAccount(null);
  };
  return (
    <div className="p-6 space-y-6">
      <div className="flex md:flex-row md:justify-between md:items-center flex-col">
        <h2 className="text-2xl font-bold">Cuentas</h2>
        <div>
          <Button className="mr-4" onClick={() => setOcultar(!ocultar)}>
            {ocultar ? (
              <>
                <Eye /> Mostrar{" "}
              </>
            ) : (
              <>
                <EyeClosed /> Ocultar{" "}
              </>
            )}
          </Button>
          {canCreateAccount && (
            <CreateAccount
              open={open}
              setOpen={limpiar}
              editAccount={editAccount}
              fetchAccounts={fetchAccounts}
              limpiar={limpiar}
            ></CreateAccount>
          )}
        </div>
      </div>
      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <>
          {Object.entries(accounts).length === 0 ? (
            <Empty
              title="Crear cuenta"
              message="Aún no tienes cuentas registradas."
            >
              <CreateAccount
                open={open}
                setOpen={limpiar}
                editAccount={editAccount}
                fetchAccounts={fetchAccounts}
                limpiar={limpiar}
              ></CreateAccount>
            </Empty>
          ) : (
            <>
              {Object.entries(
                accounts.reduce((acc, account) => {
                  const type = account.account_types?.name || "Sin tipo";
                  if (!acc[type]) acc[type] = [];
                  acc[type].push(account);
                  return acc;
                }, {})
              ).map(([type, groupedAccounts]) => (
                <div
                  key={type}
                  className={`  max-w-auto mb-6 ${ocultar ? "ocultar" : ""}`}
                >
                  <h3 className="text-xl font-bold mb-2">{type}</h3>
                  <div className="overflow-auto">
                    <div className="grid grid-cols-1">
                      <div className="col-start-1 gap-4 row-start-1 flex flex-nowrap ">
                        {groupedAccounts.map((acc: any) => (
                          <Card
                            key={acc.id}
                            className={`relative cursor-pointer ${acc?.name.toLowerCase()}`}
                            style={{ minWidth: 300 }}
                          >
                            <CardContent className="p-4 space-y-2 min-h-[150px]">
                              <div className="flex justify-between items-start">
                                <div
                                  onClick={() =>
                                    navigate(`/accounts/${acc.id}`)
                                  }
                                >
                                  <h3 className="text-lg font-semibold">
                                    {acc.name}
                                  </h3>

                                  <img src="" alt="" />
                                </div>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <span>
                                      <MoreVertical className="h-4 w-4" />
                                    </span>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() =>
                                        navigate(`/accounts/${acc.id}`)
                                      }
                                    >
                                      Ver movimientos
                                    </DropdownMenuItem>
                                    {status === "active" && (
                                      <DropdownMenuItem
                                        onClick={() => {
                                          setTimeout(() => onEdit(acc), 100);
                                        }}
                                      >
                                        Editar
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setTimeout(() => onDelete(acc.id), 100);
                                      }}
                                      className="text-red-500"
                                    >
                                      Eliminar
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              {acc.credit_limit > 0 && (
                                <p className="amount text-sm">
                                  Límite de crédito: {acc.credit_limit}
                                </p>
                              )}
                              {acc.initial_balance > 0 && (
                                <p className="amount text-sm">
                                  Saldo inicial: {acc.initial_balance}
                                </p>
                              )}
                              {(acc.billing_day || acc.due_day) && (
                                <p className="amount text-sm">
                                  Corte: {acc.billing_day} / Venc: {acc.due_day}
                                </p>
                              )}
                              <p className="amount text-sm font-semibold">
                                Saldo actual: <br></br>
                                <span className="text-lg ">
                                  {acc.currency}{" "}
                                  {balances[acc.id]?.toFixed(2) || "0.00"}
                                </span>
                              </p>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </>
      )}

      <DeteleAccount
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
        fetchAccounts={fetchAccounts}
      ></DeteleAccount>
    </div>
  );
}
